/**
 * Request helpers for projectManagementThunk — query params, FormData, JSON bodies only.
 * Response mapping lives in Pages/Projects/projectMappers.js and Redux slices.
 */
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { toProjectStatusApi } from "../../Pages/Projects/projectManagementNormalizers";
import { KANBAN_DEFAULT_LIMIT } from "../../constants";

/** Query key for multiselect reporter filter (CSV) on work item list GETs — keep in sync with API. */
export const WORK_ITEMS_REPORTER_IDS_QUERY_PARAM = "reporterIds";

/** Query/body key — `showEpics=true` includes epics; `showEpics=false` excludes them. */
export const WORK_ITEMS_SHOW_EPICS_QUERY_PARAM = "showEpics";

/** Query/body key — `showOnHold=true` includes the On Hold Kanban column (GET `/work-items/kanban/initial`). */
export const WORK_ITEMS_SHOW_ON_HOLD_QUERY_PARAM = "showOnHold";

/** @param {Record<string, unknown>} target — query or POST body */
export function applyShowEpicsFilterParam(target, showEpics) {
  if (showEpics === undefined || showEpics === null) return;
  const include = showEpics === true || showEpics === "true";
  target[WORK_ITEMS_SHOW_EPICS_QUERY_PARAM] = include;
}

/** @param {Record<string, unknown>} target — query or POST body */
export function applyShowOnHoldFilterParam(target, showOnHold) {
  if (showOnHold === true || showOnHold === "true") {
    target[WORK_ITEMS_SHOW_ON_HOLD_QUERY_PARAM] = true;
  }
}

/** Query/body key — `hideDone=true` excludes CLOSED/DONE-category work items (All / My work items lists). */
export const WORK_ITEMS_HIDE_DONE_QUERY_PARAM = "hideDone";

/** @param {Record<string, unknown>} target — query or POST body */
export function applyHideDoneFilterParam(target, hideDone) {
  if (hideDone === undefined || hideDone === null) return;
  const exclude = hideDone === true || hideDone === "true";
  target[WORK_ITEMS_HIDE_DONE_QUERY_PARAM] = exclude;
}

/** Query key for Kanban quick filter / participant scope (CSV user ids) — keep in sync with API. */
export const WORK_ITEMS_PARTICIPANT_USER_IDS_QUERY_PARAM = "participantUserIds";

function trimStr(v) {
  return v != null && String(v).trim() !== "" ? String(v).trim() : "";
}

function joinCsv(val) {
  if (val == null || val === "") return null;
  return Array.isArray(val) ? val.join(",") : val;
}

import { DEFAULT_LIST_PAGE_SIZE } from "../../Utility/Pagination/listPaginationConstants";

/** Projects list table — default page size (GET /projects `limit`). */
export const PROJECTS_LIST_PAGE_SIZE = DEFAULT_LIST_PAGE_SIZE;

export function projectsListEndpoint(getAll) {
  return getAll ? API_ENDPOINTS.PROJECTS.GET_ALL_LIST : API_ENDPOINTS.PROJECTS.GET_ALL;
}

export function buildProjectsListQuery(params, getAll) {
  if (getAll) return {};
  const q = { page: params.page ?? 1, limit: params.limit ?? PROJECTS_LIST_PAGE_SIZE };
  const cid = trimStr(params.clientId);
  if (cid) q.clientId = cid;
  const cname = trimStr(params.clientName);
  if (cname) q.clientName = cname;
  const pname = trimStr(params.projectName);
  if (pname) q.projectName = pname;
  const st = trimStr(params.status);
  if (st) q.status = toProjectStatusApi(params.status);
  return q;
}

export function resolveProjectIdEndpoint(config, id) {
  return typeof config === "function" ? config(id) : `${config}/${id}`;
}

/** One in-flight slot for global `/work-items`, `/work-items/all`, and `/work-items/my` list GETs (tab switch cancels the other). */
export const WORK_ITEMS_GLOBAL_OR_MY_LIST_SLOT_KEY = "slot:work-items-global-or-my-list";

export function buildGlobalWorkItemsQuery(params) {
  const q = { page: params.page ?? 1, limit: params.limit ?? 500 };
  const pid = joinCsv(params.projectId) ?? trimStr(params.projectId);
  if (pid) q.projectId = pid;
  const cid = joinCsv(params.clientId) ?? trimStr(params.clientId);
  if (cid) q.clientId = cid;
  const s = joinCsv(params.statusIds);
  if (s) q.statusIds = s;
  const statusCat = Array.isArray(params.statusCategories)
    ? joinCsv(params.statusCategories)
    : trimStr(params.statusCategories);
  if (statusCat) q.statusCategories = statusCat;
  const p = joinCsv(params.priorityIds);
  if (p) q.priorityIds = p;
  const t = joinCsv(params.typeIds);
  if (t) q.typeIds = t;
  const aid = joinCsv(params.assigneeId) ?? trimStr(params.assigneeId);
  if (aid) q.assigneeId = aid;
  const rid =
    joinCsv(params[WORK_ITEMS_REPORTER_IDS_QUERY_PARAM]) ??
    trimStr(params[WORK_ITEMS_REPORTER_IDS_QUERY_PARAM]);
  if (rid) q[WORK_ITEMS_REPORTER_IDS_QUERY_PARAM] = rid;
  const puid =
    joinCsv(params[WORK_ITEMS_PARTICIPANT_USER_IDS_QUERY_PARAM]) ??
    trimStr(params[WORK_ITEMS_PARTICIPANT_USER_IDS_QUERY_PARAM]);
  if (puid) q[WORK_ITEMS_PARTICIPANT_USER_IDS_QUERY_PARAM] = puid;
  const cf = trimStr(params.createdFrom);
  if (cf) q.createdFrom = cf;
  const ct = trimStr(params.createdTo);
  if (ct) q.createdTo = ct;
  const df = trimStr(params.dueFrom);
  if (df) q.dueFrom = df;
  const dto = trimStr(params.dueTo);
  if (dto) q.dueTo = dto;
  const search = trimStr(params.search);
  if (search) q.search = search;
  const teams = joinCsv(params.teamIds);
  if (teams) q.teamIds = teams;
  const cur = trimStr(params.cursor);
  if (cur) q.cursor = cur;
  applyShowEpicsFilterParam(q, params.showEpics);
  applyShowOnHoldFilterParam(q, params.showOnHold);
  applyHideDoneFilterParam(q, params.hideDone);
  return q;
}

/** Same query keys as {@link buildGlobalWorkItemsQuery} but `page` / `limit` omitted (GET /work-items/all). */
export function buildGlobalWorkItemsAllQuery(params = {}) {
  const q = buildGlobalWorkItemsQuery(params);
  delete q.page;
  delete q.limit;
  return q;
}

export function buildMyWorkItemsQuery(params) {
  const q = { page: params.page ?? 1, limit: params.limit ?? KANBAN_DEFAULT_LIMIT };
  const pid = joinCsv(params.projectId) ?? trimStr(params.projectId);
  if (pid) q.projectId = pid;
  const cid = joinCsv(params.clientId) ?? trimStr(params.clientId);
  if (cid) q.clientId = cid;
  const s = joinCsv(params.statusIds);
  if (s) q.statusIds = s;
  const statusCat = Array.isArray(params.statusCategories)
    ? joinCsv(params.statusCategories)
    : trimStr(params.statusCategories);
  if (statusCat) q.statusCategories = statusCat;
  const t = joinCsv(params.typeIds);
  if (t) q.typeIds = t;
  const p = joinCsv(params.priorityIds);
  if (p) q.priorityIds = p;
  const aid = joinCsv(params.assigneeId) ?? trimStr(params.assigneeId);
  if (aid) q.assigneeId = aid;
  const rid =
    joinCsv(params[WORK_ITEMS_REPORTER_IDS_QUERY_PARAM]) ??
    trimStr(params[WORK_ITEMS_REPORTER_IDS_QUERY_PARAM]);
  if (rid) q[WORK_ITEMS_REPORTER_IDS_QUERY_PARAM] = rid;
  const teams = joinCsv(params.teamIds);
  if (teams) q.teamIds = teams;
  const cf = trimStr(params.createdFrom);
  if (cf) q.createdFrom = cf;
  const ct = trimStr(params.createdTo);
  if (ct) q.createdTo = ct;
  const db = trimStr(params.dueBefore);
  if (db) q.dueBefore = db;
  const da = trimStr(params.dueAfter);
  if (da) q.dueAfter = da;
  const df = trimStr(params.dueFrom);
  if (df) q.dueFrom = df;
  const dto = trimStr(params.dueTo);
  if (dto) q.dueTo = dto;
  const search = trimStr(params.search);
  if (search) q.search = search;
  applyShowEpicsFilterParam(q, params.showEpics);
  applyHideDoneFilterParam(q, params.hideDone);
  return q;
}

/** Same query keys as {@link buildMyWorkItemsQuery} but `page` / `limit` omitted (GET /work-items/my/all). */
export function buildMyWorkItemsAllQuery(params = {}) {
  const q = buildMyWorkItemsQuery(params);
  delete q.page;
  delete q.limit;
  return q;
}

/**
 * Shared filter serialization for GET /projects/:id/work-items and export — no pagination defaults.
 */
function applyProjectWorkItemFiltersToQuery(q, body = {}) {
  const search = trimStr(body.search);
  if (search) q.search = search;
  const s = joinCsv(body.statusIds);
  if (s) q.statusIds = s;
  const statusCat = Array.isArray(body.statusCategories)
    ? joinCsv(body.statusCategories)
    : trimStr(body.statusCategories);
  if (statusCat) q.statusCategories = statusCat;
  const p = joinCsv(body.priorityIds);
  if (p) q.priorityIds = p;
  const t = joinCsv(body.typeIds);
  if (t) q.typeIds = t;
  const aid = joinCsv(body.assigneeId) ?? trimStr(body.assigneeId);
  if (aid) q.assigneeId = aid;
  const rid =
    joinCsv(body[WORK_ITEMS_REPORTER_IDS_QUERY_PARAM]) ??
    trimStr(body[WORK_ITEMS_REPORTER_IDS_QUERY_PARAM]);
  if (rid) q[WORK_ITEMS_REPORTER_IDS_QUERY_PARAM] = rid;
  const teams = joinCsv(body.teamIds);
  if (teams) q.teamIds = teams;
  const parentIdsCsv = joinCsv(body.parentIds);
  if (parentIdsCsv) q.parentIds = parentIdsCsv;
  if (body.hideDone != null) {
    q.hideDone = body.hideDone === true || String(body.hideDone).toLowerCase() === "true";
  }
  const cur = trimStr(body.cursor);
  if (cur) q.cursor = cur;
  applyShowEpicsFilterParam(q, body.showEpics);
}

export function buildProjectWorkItemsQuery(body = {}) {
  const q = { limit: body.limit ?? KANBAN_DEFAULT_LIMIT };
  if (body.page != null) q.page = body.page;
  applyProjectWorkItemFiltersToQuery(q, body);
  return q;
}

/** GET /work-items/:projectId with parentIds — Issue detail epic children panel. */
export function normalizeEpicParentIdFromFetchArg(parentIds) {
  if (Array.isArray(parentIds)) {
    const first = parentIds.find((id) => id != null && String(id).trim() !== "");
    return first != null ? String(first).trim() : "";
  }
  const csv = String(parentIds ?? "").trim();
  if (!csv) return "";
  return csv.split(",")[0].trim();
}

export function isEpicChildWorkItemsFetchArg(arg) {
  if (!arg?.skipIssuesSlice) return false;
  return Boolean(normalizeEpicParentIdFromFetchArg(arg?.body?.parentIds));
}

/** GET /projects/:id/work-items/export — same filters as list, without page/limit. */
export function buildProjectWorkItemsExportQueryParams(filterBody = {}) {
  const q = {};
  applyProjectWorkItemFiltersToQuery(q, filterBody);
  return q;
}

export function buildWorkItemAttachmentsFormData(files) {
  const formData = new FormData();
  (files || []).forEach((file, index) => {
    if (!file) return;
    formData.append("files", file, file.name || `file-${index}`);
  });
  return formData;
}

/** POST create / PATCH update — multipart with JSON `data` + `files`. */
export function buildWorkItemWithAttachmentsFormData(body, files) {
  const formData = new FormData();
  formData.append("data", JSON.stringify(body ?? {}));
  (files || []).forEach((file, index) => {
    if (!file) return;
    formData.append("files", file, file.name || `file-${index}`);
  });
  return formData;
}

/** @deprecated Use {@link buildWorkItemWithAttachmentsFormData} */
export const buildCreateWorkItemWithAttachmentsFormData = buildWorkItemWithAttachmentsFormData;

export function buildAddCommentBody(comment, mentionedUserIds) {
  const body = { comment: String(comment || "").trim() };
  if (Array.isArray(mentionedUserIds) && mentionedUserIds.length > 0) {
    body.mentionedUserIds = [
      ...new Set(mentionedUserIds.map((id) => String(id).trim()).filter(Boolean)),
    ];
  }
  return body;
}

/** POST/PATCH comment with attachments — multipart, JSON comment body under "data", files under "files". */
export function buildAddCommentWithAttachmentsFormData(comment, mentionedUserIds, files) {
  const body = buildAddCommentBody(comment, mentionedUserIds);
  return buildWorkItemWithAttachmentsFormData(body, files);
}

/** POST /projects/.../time-logs — `billableMinutes` is set via PATCH after create when needed. */
export function buildAddTimeLogBody({ minutesLogged, logDate, comment }) {
  return {
    minutesLogged: Number(minutesLogged) || 0,
    logDate: logDate || null,
    comment: comment ? String(comment).trim() : null,
  };
}
